/**
 * "Test this site" from a site's ⋯ menu, its page header or its "Tests of this
 * site" card: make a test copy of the site's live version (UserTesting/
 * testCopy.js, the same step as New test > One of your sites) and land on the
 * new test's Setup with the "Test created from …" note.
 *
 * The test is its own copy with its own link (DECISIONS 23 Oct): updating the
 * site never changes it, and "Upload to this test" updates the test only.
 * Every click makes a new copy, so a site can have several tests.
 *
 * Only for HTML sites with a live version that the person owns (a copy is a
 * new doc in the owner's account, which a collaborator cannot create), and
 * never for a test copy itself.
 */
import { buildTestCreateActions } from '../UserTesting/createFlowActions';
import { isTestCopy } from '../UserTesting/testCopy';

export function canTestSite(site) {
  return !!site && site.type === 'html' && !!site.activeVersion && !site.shared && !isTestCopy(site);
}

/** An older test kept on the site's own doc (before copies). */
export function siteHasTest(site) {
  return !!(site && site.userTesting && site.userTesting.enabled === true);
}

/**
 * The ⋯ item for a site: "Test this site" whenever it can be copied; a site
 * that cannot (shared) but has its own older test gets "Open its test".
 */
export function testMenuItem(site, onTest) {
  if (canTestSite(site)) {
    return {
      key: 'test',
      label: 'Test this site',
      desc: 'Makes a copy with its own test link. Your site doesn’t change.',
      onClick: () => onTest(site),
    };
  }
  if (siteHasTest(site)) return { key: 'test', label: 'Open its test', onClick: () => onTest(site) };
  return null;
}

/**
 * Run it. `host` is the site's address as shown ("pocket.aeroapp.ai"), for the
 * "Test created from …" note on Setup. Returns after navigating; throws when
 * the write fails so the caller can show its error toast.
 */
export async function testThisSite({ uid, site, host, navigate, refresh }) {
  if (!canTestSite(site)) {
    if (siteHasTest(site)) {
      navigate(`/user-testing/${site.id}`);
      return;
    }
    throw new Error('This site cannot be tested yet');
  }
  if (!uid) throw new Error('Not signed in');
  const { landing } = await buildTestCreateActions(uid).finishPublishedSite({
    siteId: site.id, tasks: [], siteHost: host || null,
  });
  if (refresh) refresh();
  navigate(landing, { state: { createdFromSite: host || null } });
}
